import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import type { FormInputs } from "./form-validation/user-login.schema";
import { defaultValues as loginDefaultValues, formatFormDataToBodyData, schema as loginSchema } from "./form-validation/user-login.schema";
import type { FormInputs as ResetPasswordFormInputs } from "./form-validation/user-reset-password.schema";
import {
  defaultValues as resetPasswordDefaultValues,
  formatFormDataToBodyData as ResetPasswordFormatFormDataToBodyData,
  schema as resetPasswordSchema,
} from "./form-validation/user-reset-password.schema";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { loginUser } from "../../../write/application/use-cases/user-login/login-user";
import { resetLoginUserState } from "../../../write/application/use-cases/user-login/login-user-reducer";
import { selectUserLogin } from "@user-management-context/write/application/use-cases/user-login/selectors/user-login-selectors";
import LoginForm from "./login-form";
import ResetForm from "./reset-form";
import CheckPage from "./check-page";
import { useUserPasswordReset } from "./use-user-password-reset";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { resolveLandingUrl } from "@user-management-context/shared/domain/landing-url";

type STEP = "login" | "reset" | "check";
interface LocationState {
  from?: string;
}

const LoginPage = () => {
  const [step, setStep] = useState<STEP>("login");
  const [email, setEmail] = useState("");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  // Read only: the toast for this outcome is raised by App, which outlives the
  // redirect that unmounts this page on success.
  const { processing } = useAppSelector(selectUserLogin);
  const { user, activeRole } = useSession();
  const { requestPasswordReset } = useUserPasswordReset();
  // Formulaires des thunks de session : leur issue est rendue par App (use-login-notifications) et l'écran de vérification
  const loginForm = useForm<FormInputs>({ resolver: yupResolver(loginSchema), defaultValues: loginDefaultValues });
  const resetPasswordForm = useForm<ResetPasswordFormInputs>({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: resetPasswordDefaultValues,
  });

  useEffect(() => {
    if (processing !== "success" || !user) return;
    const redirect = (state as Nullable<LocationState>)?.from ?? null;
    const target = resolveLandingUrl({ activeRole, isAdmin: user.isAdmin, redirect });
    if (target) navigate(target);
  }, [processing, navigate, state, user, activeRole]);

  useEffect(() => {
    return () => {
      dispatch(resetLoginUserState());
    };
  }, [dispatch]);

  const onLogin = (formBody: FormInputs) => {
    const body = formatFormDataToBodyData(formBody);
    dispatch(loginUser(body));
  };

  // Chaque étape repart d'un formulaire vide, comme quand elle était montée avec son propre formulaire
  const onForgetPassword = () => {
    resetPasswordForm.reset();
    setStep("reset");
  };
  const onPasswordReset = (formBody: ResetPasswordFormInputs) => {
    const body = ResetPasswordFormatFormDataToBodyData(formBody);
    void requestPasswordReset(body);
    setEmail(formBody.email);
    setStep("check");
  };
  const onBackToLogin = () => {
    loginForm.reset();
    setStep("login");
  };

  return (
    <div className="flex items-center justify-center">
      {step === "login" && (
        <LoginForm
          form={loginForm}
          processing={processing}
          onSubmit={loginForm.handleSubmit(onLogin)}
          onDisplayResetPasswordForm={onForgetPassword}
        />
      )}
      {step === "reset" && (
        <ResetForm form={resetPasswordForm} onSubmit={resetPasswordForm.handleSubmit(onPasswordReset)} onBack={onBackToLogin} />
      )}
      {step === "check" && <CheckPage onBack={onBackToLogin} email={email} />}
    </div>
  );
};

export default LoginPage;
